import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import SearchableSelect from '../SearchableSelect';
import { notify } from '../common/AppToast';
import { AppButton, AppFormLabel, AppInput } from '../ui';
import {
  buildPlanDeComSupportOptionsByType,
  classifyPlanDeComSupport,
  decodePlanDeComSupportValue,
  extractPlanDeComTarifLibelle,
  formatPlanDeComOptionToastMessage,
  PDC_SUPPORT_TYPE_GROUPS,
  planDeComMoisToNumber,
  planDeComSupportTypeKey,
} from '../../utils/planDeComSupportUtils';
import { MOIS_LABELS } from '../../utils/planDeComWarnings';
import {
  formatPlanDeComMoney,
  getPlanDeComLigneFraisTotal,
  getPlanDeComLigneSupportGroups,
  getPlanDeComLigneSupportItems,
  getPlanDeComLigneTotal,
  getPlanDeComSupportFeeField,
  isPlanDeComLigneFilled,
  monthTitle,
  syncLigneSupports,
} from '../../utils/planDeComCalendrierUi';

const DEFAULT_TYPE = PDC_SUPPORT_TYPE_GROUPS[0].key;

const TYPE_META = Object.fromEntries(
  PDC_SUPPORT_TYPE_GROUPS.map((group) => [group.key, group]),
);

const isWebFeeLabel = (label) => /banni[eè]re|site|agenda/i.test(String(label ?? ''));

const supportChipLabel = (group) => {
  if (group?.typeKey !== 'set') {
    return group?.tarifLibelle || group?.displayLabel;
  }
  const full = String(group?.label || '').trim();
  const withoutPeriod = full.replace(/ — [a-zàâäéèêëïîôùûüç]+ \d{4}$/iu, '').trim();
  return withoutPeriod || group?.tarifLibelle || group?.displayLabel;
};

const getFeeOptionsForSupportType = (frais = [], typeKey = DEFAULT_TYPE) => {
  const labels = Array.isArray(frais) ? frais : [];
  const filtered = labels.filter((label) => {
    const webFee = isWebFeeLabel(label);
    if (typeKey === 'web' || typeKey === 'newsletter') return webFee;
    return !webFee;
  });
  const source = filtered.length > 0 ? filtered : labels;
  return source.map((label) => ({ value: label, label }));
};

const PlanDeComMonthEditor = ({
  show,
  ligne,
  meta,
  disponibilitesByKey = {},
  disponibilitesLoading = false,
  onClose,
  onChangeField,
  onClearMonth,
}) => {
  const [activeType, setActiveType] = useState(DEFAULT_TYPE);

  useEffect(() => {
    if (!show) {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [show, onClose]);

  useEffect(() => {
    setActiveType(DEFAULT_TYPE);
  }, [ligne?.ordre, ligne?.mois, ligne?.annee]);

  const supportItems = useMemo(() => getPlanDeComLigneSupportItems(ligne), [ligne]);
  const supportGroups = useMemo(() => getPlanDeComLigneSupportGroups(ligne), [ligne]);

  const enrichedGroups = useMemo(
    () => supportGroups.map((group) => {
      const typeKey = planDeComSupportTypeKey(
        classifyPlanDeComSupport(group.tarifLibelle || group.label),
      );
      const decoded = decodePlanDeComSupportValue(group.value);
      return {
        ...group,
        typeKey,
        typeLabel: TYPE_META[typeKey]?.label || 'Support',
        displayLabel: decoded.label,
        feeField: group.feeField || getPlanDeComSupportFeeField(group.field),
        feeLabel: group.feeLabel || '',
      };
    }),
    [supportGroups],
  );

  const selectedValues = useMemo(
    () => supportItems.map((item) => item.value),
    [supportItems],
  );

  const month = planDeComMoisToNumber(ligne?.mois);
  const cacheKey = ligne?.annee && month ? `${ligne.annee}-${month}` : null;
  const disponibilites = cacheKey ? disponibilitesByKey[cacheKey] : null;

  const optionsByType = useMemo(() => {
    if (!disponibilites) {
      return Object.fromEntries(PDC_SUPPORT_TYPE_GROUPS.map((group) => [group.key, []]));
    }
    return buildPlanDeComSupportOptionsByType(disponibilites, {
      excludeValues: selectedValues,
    });
  }, [disponibilites, selectedValues]);

  const activeGroup = TYPE_META[activeType] || PDC_SUPPORT_TYPE_GROUPS[0];
  const activeOptions = optionsByType[activeType] ?? [];
  const fraisTechniques = meta?.frais ?? [];
  const fraisTotal = getPlanDeComLigneFraisTotal(ligne);
  const totalDuMois = getPlanDeComLigneTotal(ligne);

  if (!show || !ligne) return null;

  const isStartPeriodEditor = ligne.ordre === 0;
  const monthOptions = MOIS_LABELS.map((item) => ({ value: item, label: monthTitle(item) }));
  const selectKey = `add-${ligne.ordre}-${activeType}-${selectedValues.join('|')}`;

  const commitSupports = (items) => {
    const next = syncLigneSupports(ligne, items);
    onChangeField?.(ligne.ordre, 'supports', next.supports);
  };

  const handleAddSupport = (value) => {
    if (!value) return;
    const option = activeOptions.find((item) => String(item.value) === String(value));

    if (option?.is_option) {
      notify.warning(formatPlanDeComOptionToastMessage(option), { autoClose: 6500 });
    }

    commitSupports([
      ...supportItems.map((item) => ({ value: item.value, frais: item.feeLabel })),
      { value, frais: null },
    ]);
  };

  const handleRemoveGroup = (group) => {
    if (!group?.items?.length) return;
    const removeIndex = group.items[group.items.length - 1].index;
    commitSupports(
      supportItems
        .filter((item) => item.index !== removeIndex)
        .map((item) => ({ value: item.value, frais: item.feeLabel })),
    );
  };

  const handleIncrementGroup = (group) => {
    const tarif = group.tarifLibelle || extractPlanDeComTarifLibelle(group.value);
    const typeKey = group.typeKey || DEFAULT_TYPE;
    const options = optionsByType[typeKey] ?? [];
    const nextOption = options.find((option) => (
      (option.tarifLibelle || extractPlanDeComTarifLibelle(option.value)) === tarif
    ));
    if (!nextOption?.value) return;
    handleAddSupport(nextOption.value);
  };

  const handleFeeChange = (group, value) => {
    const firstIndex = group.items?.[0]?.index;
    if (firstIndex == null) return;
    commitSupports(
      supportItems.map((item) => ({
        value: item.value,
        frais: item.index === firstIndex ? (value || null) : item.feeLabel,
      })),
    );
  };

  return (
    <div className="kkp-pdc-month-editor-backdrop" role="presentation" onClick={onClose}>
      <aside
        className="kkp-pdc-month-editor"
        role="dialog"
        aria-modal="true"
        aria-labelledby="kkp-pdc-month-editor-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="kkp-pdc-month-editor__header">
          <div>
            <p className="kkp-pdc-month-editor__eyebrow mb-1">Mois</p>
            <h3 id="kkp-pdc-month-editor-title" className="kkp-pdc-month-editor__title">
              {monthTitle(ligne.mois)} {ligne.annee}
            </h3>
            <p className="kkp-pdc-month-editor__subtitle mb-0">
              Choisissez un type, puis ajoutez des supports. Les produits mag illimités se cumulent (x2, x3). Chaque emplacement set (carré, bandeau…) se choisit à part.
            </p>
          </div>
          <button type="button" className="btn-close" aria-label="Fermer" onClick={onClose} />
        </div>

        <div className="kkp-pdc-month-editor__body">
          {isStartPeriodEditor ? (
            <div className="kkp-pdc-month-editor__period-block">
              <div className="kkp-pdc-month-editor__period">
                <div>
                  <AppFormLabel>Premier mois</AppFormLabel>
                  <SearchableSelect
                    value={ligne.mois ?? ''}
                    onChange={(nextValue) => onChangeField?.(ligne.ordre, 'mois', nextValue)}
                    options={monthOptions}
                    placeholder="Choisir un mois…"
                    emptyLabel="Aucun mois trouvé"
                    allowEmpty={false}
                  />
                </div>
                <div>
                  <AppFormLabel htmlFor={`pdc-month-year-${ligne.ordre}`}>Année</AppFormLabel>
                  <AppInput
                    id={`pdc-month-year-${ligne.ordre}`}
                    type="number"
                    value={ligne.annee ?? ''}
                    onChange={(e) => onChangeField?.(ligne.ordre, 'annee', Number(e.target.value))}
                  />
                </div>
              </div>
              <p className="kkp-pdc-month-editor__period-hint mb-0">
                {isPlanDeComLigneFilled(ligne)
                  ? 'Pour décaler le début, videz d’abord ce mois (les campagnes des mois suivants seront conservées).'
                  : 'Changer le premier mois décale la fenêtre du calendrier : les campagnes restent sur leur mois, un mois vide est ajouté à la fin (ou au début si vous avancez).'}
              </p>
            </div>
          ) : null}

          <div className="mb-3">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <label className="form-label mb-0">Supports</label>
              <span className="kkp-pdc-month-editor__count">{supportItems.length}</span>
            </div>

            {enrichedGroups.length > 0 ? (
              <ul className="kkp-pdc-month-editor__chips">
                {enrichedGroups.map((group) => (
                  <li key={group.key} className="kkp-pdc-month-editor__chip">
                    <div className="kkp-pdc-month-editor__chip-main">
                      <span className={`kkp-pdc-month-editor__chip-type is-${group.typeKey}`}>
                        {group.typeLabel}
                      </span>
                      <span className="kkp-pdc-month-editor__chip-copy">
                        <span className="kkp-pdc-month-editor__chip-label" title={supportChipLabel(group)}>
                          {supportChipLabel(group)}
                        </span>
                        {group.dateLabel ? (
                          <span className="kkp-pdc-month-editor__chip-date">{group.dateLabel}</span>
                        ) : null}
                      </span>
                      {group.qty > 1 ? (
                        <span className="kkp-pdc-month-editor__chip-qty">x{group.qty}</span>
                      ) : null}
                      {group.repeatable ? (
                        <button
                          type="button"
                          className="kkp-pdc-month-editor__chip-qty-btn"
                          aria-label={`Ajouter un ${group.displayLabel}`}
                          disabled={!(optionsByType[group.typeKey] ?? []).some((option) => (
                            (option.tarifLibelle || extractPlanDeComTarifLibelle(option.value)) === group.tarifLibelle
                          ))}
                          onClick={() => handleIncrementGroup(group)}
                        >
                          <Icon icon="mdi:plus" />
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="kkp-pdc-month-editor__chip-remove"
                        aria-label={`Retirer ${group.displayLabel}`}
                        onClick={() => handleRemoveGroup(group)}
                      >
                        <Icon icon="mdi:close" />
                      </button>
                    </div>
                    <div className="kkp-pdc-month-editor__chip-extra">
                      {group.remises?.length ? (
                        <div className="d-flex flex-wrap gap-1 mb-2">
                          {group.remises.map((remise) => (
                            <span key={`${group.key}-${remise.key}-${remise.label}`} className="badge bg-warning-100 text-warning-600">
                              {remise.label}
                            </span>
                          ))}
                        </div>
                      ) : null}
                      <label className="form-label">Frais technique (optionnel, hors remise)</label>
                      <SearchableSelect
                        id={`pdc-fee-${group.key}-${ligne.ordre}`}
                        value={group.feeLabel ?? ''}
                        onChange={(nextValue) => handleFeeChange(group, nextValue)}
                        options={getFeeOptionsForSupportType(fraisTechniques, group.typeKey)}
                        placeholder="Choisir un frais technique…"
                        emptyLabel="Aucun frais technique disponible"
                        allowEmpty
                        emptyOptionLabel="Aucun frais technique"
                        buttonClassName="form-select form-select-sm text-start kkp-pdc-month-editor__fee-select"
                      />
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="kkp-pdc-month-editor__empty-hint">
                Aucun support pour l’instant.
              </p>
            )}

            <div className="kkp-pdc-month-editor__add">
              <div
                className="kkp-pdc-month-editor__type-switch"
                role="tablist"
                aria-label="Type de support"
              >
                {PDC_SUPPORT_TYPE_GROUPS.map((group) => {
                  const isActive = activeType === group.key;
                  const count = (optionsByType[group.key] ?? []).length;
                  return (
                    <button
                      key={group.key}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      className={`kkp-pdc-month-editor__type-btn is-${group.key}${isActive ? ' is-active' : ''}`}
                      onClick={() => setActiveType(group.key)}
                    >
                      <span>{group.label}</span>
                      {disponibilites ? (
                        <span className="kkp-pdc-month-editor__type-btn-count">{count}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>

              <SearchableSelect
                id={`pdc-support-${activeType}-${ligne.ordre}`}
                key={selectKey}
                value=""
                onChange={handleAddSupport}
                options={activeOptions}
                placeholder={
                  disponibilitesLoading && !disponibilites
                    ? 'Chargement…'
                    : `Choisir un ${activeGroup.label.toLowerCase()}…`
                }
                emptyLabel={`Aucun ${activeGroup.label.toLowerCase()} disponible`}
                allowEmpty={false}
                disabled={disponibilitesLoading && !disponibilites}
              />
            </div>
          </div>

          <div className="kkp-pdc-month-editor__amounts">
            <div className="kkp-pdc-month-editor__amounts-row">
              <div className="kkp-pdc-month-editor__amount">
                <span>Prix catalogue</span>
                <strong>{formatPlanDeComMoney(ligne.calcules?.V ?? ligne.calcules?.F)}</strong>
              </div>
              <div className="kkp-pdc-month-editor__amount">
                <span>Après remises</span>
                <strong>{formatPlanDeComMoney(ligne.calcules?.X ?? ligne.calcules?.W)}</strong>
              </div>
              <div className="kkp-pdc-month-editor__amount">
                <span>Frais techniques</span>
                <strong>{formatPlanDeComMoney(fraisTotal)}</strong>
              </div>
            </div>
            <div className="kkp-pdc-month-editor__amount kkp-pdc-month-editor__amount--total">
              <span>Total du mois</span>
              <strong>{formatPlanDeComMoney(totalDuMois)}</strong>
            </div>
          </div>
        </div>

        <div className="kkp-pdc-month-editor__footer">
          <AppButton
            type="button"
            variant="outline-danger"
            onClick={() => onClearMonth?.(ligne.ordre)}
          >
            Effacer ce mois
          </AppButton>
          <AppButton type="button" variant="primary" onClick={onClose}>
            Terminé
          </AppButton>
        </div>
      </aside>
    </div>
  );
};

export default PlanDeComMonthEditor;
